import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { isInstalledPWA } from '@/lib/notificationService';

interface SWUpdateState {
  needRefresh: boolean;
  isChecking: boolean;
  registration: ServiceWorkerRegistration | null;
}

// Check if we're on mobile browser (not installed PWA)
const isMobileBrowser = () => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  return isMobile && !isInstalledPWA();
};

export function useSWUpdate() {
  const [state, setState] = useState<SWUpdateState>({
    needRefresh: false,
    isChecking: false,
    registration: null,
  });
  const lastCheckRef = useRef<number>(0);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      console.log('[SW Update] Service Worker not supported');
      return;
    }

    // On mobile browser, don't auto-update - just notify
    const shouldAutoReload = !isMobileBrowser();

    const registerSW = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none',
        });

        console.log('[SW Update] Service Worker registered:', registration);
        setState(prev => ({ ...prev, registration }));

        // Vérifier immédiatement s'il y a une mise à jour en attente
        if (registration.waiting) {
          console.log('[SW Update] Update waiting on registration');
          // Only set needRefresh for installed PWAs
          if (!isMobileBrowser()) {
            setState(prev => ({ ...prev, needRefresh: true }));
          }
        }

        // Écouter les nouvelles mises à jour
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('[SW Update] New worker installing');

          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[SW Update] New version ready');
              
              // Only trigger auto-update flow for installed PWAs
              if (!isMobileBrowser()) {
                setState(prev => ({ ...prev, needRefresh: true }));
                toast.success('Nouvelle version disponible !', {
                  description: 'L\'app va se mettre à jour automatiquement.',
                  duration: 3000,
                });
              } else {
                // For mobile browser, just skip waiting silently - next refresh will get new version
                console.log('[SW Update] Mobile browser - new version will apply on next visit');
              }
            }
          });
        });

        // Only do periodic checks for installed PWAs
        if (shouldAutoReload) {
          // Vérifier les mises à jour périodiquement (toutes les 60 secondes)
          const intervalId = setInterval(() => {
            registration.update().catch(console.error);
          }, 60 * 1000);

          // Vérifier au retour sur l'app (visibilitychange)
          const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
              const now = Date.now();
              // Ne vérifier que si la dernière vérification date de plus de 30 secondes
              if (now - lastCheckRef.current > 30000) {
                console.log('[SW Update] App became visible, checking for updates...');
                lastCheckRef.current = now;
                registration.update().catch(console.error);
              }
            }
          };

          document.addEventListener('visibilitychange', handleVisibilityChange);

          // Cleanup
          return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          };
        }

      } catch (error) {
        console.error('[SW Update] Registration failed:', error);
      }
    };

    // Écouter le message du controllerchange (quand un nouveau SW prend le contrôle)
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Only auto-reload for installed PWAs, not mobile browsers
      if (!refreshing && shouldAutoReload) {
        refreshing = true;
        console.log('[SW Update] Controller changed, reloading...');
        window.location.reload();
      } else if (!shouldAutoReload) {
        console.log('[SW Update] Mobile browser - skipping auto-reload');
      }
    });

    registerSW();
  }, []);

  const checkForUpdates = useCallback(async () => {
    if (!state.registration) {
      console.log('[SW Update] No registration available');
      return false;
    }

    setState(prev => ({ ...prev, isChecking: true }));

    try {
      await state.registration.update();
      console.log('[SW Update] Update check completed');
      
      // Attendre un peu pour laisser le temps au SW de s'installer
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const hasUpdate = !!state.registration.waiting;
      setState(prev => ({ ...prev, isChecking: false, needRefresh: hasUpdate }));
      
      return hasUpdate;
    } catch (error) {
      console.error('[SW Update] Update check failed:', error);
      setState(prev => ({ ...prev, isChecking: false }));
      return false;
    }
  }, [state.registration]);

  const updateSW = useCallback(async () => {
    if (!state.registration?.waiting) {
      console.log('[SW Update] No waiting worker');
      return;
    }

    console.log('[SW Update] Sending skip waiting message');
    state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }, [state.registration]);

  return {
    needRefresh: state.needRefresh,
    isChecking: state.isChecking,
    checkForUpdates,
    updateSW,
  };
}
